/**
 * The workbook's NTSB Recommendations register: filtering, sorting and counting the recommendations
 * tied to a workbook's reports, and formatting what the table shows as LLM context for its chat.
 *
 * How one recommendation reads (its status group, status wording and age) is shared with the
 * explorer's register: see components/safety-recommendations/recommendations.ts.
 */

import {RegisterRecommendationDTO, SafetyRecommendationStatusGroup} from '../../../../../../@types/safety-recommendation';
import {
    ageLabel,
    ageYears,
    emptyGroupRecord,
    isAddressedToFaa,
    IssuedByYear,
    STATUS_GROUP_LABELS,
} from '../../../../../../components/safety-recommendations/recommendations';
import {NtsbRegisterFilters} from '../../../../../../stores/irp-workbook.store';

/** The register chat gets letters too when the table shows at most this many recommendations. */
export const DETAILED_CONTEXT_LIMIT = 30;
/** The most recommendations the register chat is given; beyond this the user is asked to narrow the filters. */
export const CONTEXT_LIMIT = 400;
const LETTER_CHARS = 1500;
const CASE_TEXT_CHARS = 600;
const COMPACT_TEXT_CHARS = 350;

const issuedYear = (rec: RegisterRecommendationDTO): number | null =>
    rec.issuedDate ? Number(rec.issuedDate.slice(0, 4)) : null;

/** Everything but the status filter, so status counts can show what each choice would give. */
export const applyNonStatusFilters = (
    recs: RegisterRecommendationDTO[],
    filters: NtsbRegisterFilters
): RegisterRecommendationDTO[] => {
    const search = filters.search.trim().toLowerCase();
    return recs.filter((rec) => {
        if (!filters.showSuperseded && rec.superseded) return false;
        if (filters.addressedTo === 'faa' && !isAddressedToFaa(rec)) return false;
        if (filters.addressedTo === 'notFaa' && isAddressedToFaa(rec)) return false;
        if (filters.relation !== 'all' && !rec.linkedReports.some((r) => r.relation === filters.relation)) return false;
        const year = issuedYear(rec);
        if (filters.issuedFromYear !== null && (year === null || year < filters.issuedFromYear)) return false;
        if (filters.issuedToYear !== null && (year === null || year > filters.issuedToYear)) return false;
        if (search) {
            const haystack = [
                rec.number, rec.issuingCaseNumber, rec.text, rec.keywords,
                ...rec.otherAddressees.flatMap((a) => [a.name, a.acronym]),
                ...rec.linkedReports.map((r) => r.uniqueId),
            ].filter((part): part is string => Boolean(part)).join(' ').toLowerCase();
            if (!haystack.includes(search)) return false;
        }
        return true;
    });
};

export const applyRegisterFilters = (
    recs: RegisterRecommendationDTO[],
    filters: NtsbRegisterFilters
): RegisterRecommendationDTO[] => {
    const rest = applyNonStatusFilters(recs, filters);
    if (filters.statusGroups.length === 0) return rest;
    return rest.filter((rec) => filters.statusGroups.includes(rec.focusStatusGroup));
};

/** Every filter except the issue-year range: what the issue-year timeline counts. */
export const issuedByYear = (recs: RegisterRecommendationDTO[]): IssuedByYear => {
    const dated = recs
        .map((rec) => ({year: rec.issuedDate ? Number(rec.issuedDate.slice(0, 4)) : NaN, group: rec.focusStatusGroup}))
        .filter((entry) => !isNaN(entry.year));
    if (dated.length === 0) return {years: [], counts: emptyGroupRecord((): number[] => [])};
    const first = Math.min(...dated.map((entry) => entry.year));
    const last = Math.max(...dated.map((entry) => entry.year));
    const years = Array.from({length: last - first + 1}, (_, i) => first + i);
    const counts = emptyGroupRecord((): number[] => years.map(() => 0));
    dated.forEach((entry) => { counts[entry.group][entry.year - first] += 1; });
    return {years, counts};
};

export const sortRegister = (
    recs: RegisterRecommendationDTO[],
    filters: NtsbRegisterFilters,
    today: Date
): RegisterRecommendationDTO[] => {
    const direction = filters.sortDirection === 'asc' ? 1 : -1;
    const key = (rec: RegisterRecommendationDTO): string | number => {
        switch (filters.sortBy) {
            case 'yearsOpen':
                return ageYears(rec, today) ?? -1;
            case 'number':
                return rec.number;
            default:
                return rec.issuedDate ?? '';
        }
    };
    return [...recs].sort((a, b) => {
        const ka = key(a);
        const kb = key(b);
        if (ka < kb) return -direction;
        if (ka > kb) return direction;
        return a.number.localeCompare(b.number);
    });
};

export const countByGroup = (recs: RegisterRecommendationDTO[]): Record<SafetyRecommendationStatusGroup, number> => {
    const counts = emptyGroupRecord((): number => 0);
    recs.forEach((rec) => { counts[rec.focusStatusGroup] += 1; });
    return counts;
};

/** The active table filters in words, for the chat context; "none" when nothing narrows the table. */
export const describeFilters = (filters: NtsbRegisterFilters): string => {
    const parts = [
        filters.statusGroups.length > 0
            ? `status ${filters.statusGroups.map((g) => STATUS_GROUP_LABELS[g]).join(' or ')}` : null,
        filters.addressedTo === 'faa' ? 'addressed to the FAA' : null,
        filters.addressedTo === 'notFaa' ? 'not addressed to the FAA' : null,
        filters.relation === 'ISSUED' ? 'issued by these reports\' investigations' : null,
        filters.relation === 'REITERATED' ? 'reiterated after later accidents in these reports' : null,
        filters.issuedFromYear !== null ? `issued from ${filters.issuedFromYear}` : null,
        filters.issuedToYear !== null ? `issued up to ${filters.issuedToYear}` : null,
        filters.showSuperseded ? 'including superseded' : 'superseded left out',
        filters.search.trim() ? `matching "${filters.search.trim()}"` : null,
    ].filter((part): part is string => Boolean(part));
    return parts.join('; ');
};

const shorten = (text: string, limit: number): string =>
    text.length > limit ? `${text.slice(0, limit).trimEnd()}...` : text;

const oneLine = (text: string): string => text.replace(/\s+/g, ' ').trim();

const recommendationLine = (rec: RegisterRecommendationDTO, today: Date, detailed: boolean): string => {
    const status = isAddressedToFaa(rec)
        ? `FAA: ${rec.faaStatus || 'status unknown'}${rec.faaClosedDate ? ` (closed ${rec.faaClosedDate})` : ''}`
        : `Not addressed to the FAA; overall: ${rec.overallStatus || 'unknown'}`;
    const details = [
        status,
        rec.issuedDate ? `issued ${rec.issuedDate}` : null,
        ageLabel(rec, today) || null,
        rec.priority === 'CLASS I' ? 'CLASS I (urgent)' : rec.priority || null,
        rec.otherAddressees.length > 0
            ? `also to: ${rec.otherAddressees.map((a) => `${a.name} (${a.status || 'status unknown'})`).join('; ')}`
            : null,
        rec.superseded ? `superseded by ${rec.supersededBy.map((s) => s.number).join(', ')}` : null,
        rec.timesReiterated ? `reiterated ${rec.timesReiterated}x by the NTSB` : null,
    ].filter((part): part is string => Boolean(part));
    const ties = rec.linkedReports
        .map((r) => (r.relation === 'REITERATED' ? `${r.uniqueId} (reiterated it)` : `${r.uniqueId} (issued it)`))
        .join(', ');
    const text = oneLine(rec.text || '(no text)');
    const lines = [
        `- ${rec.number} [${details.join(' | ')}]`,
        `  Workbook reports tied to it: ${ties}`,
        `  Text: ${detailed ? text : shorten(text, COMPACT_TEXT_CHARS)}`,
    ];
    if (detailed && rec.assessment?.contents) {
        lines.push(`  NTSB assessment (${rec.assessment.date}): ${shorten(oneLine(rec.assessment.contents), LETTER_CHARS)}`);
    }
    if (detailed && rec.latestResponse?.contents) {
        lines.push(`  Latest response from ${rec.latestResponse.addressee || 'the addressee'} (${rec.latestResponse.date}): `
            + shorten(oneLine(rec.latestResponse.contents), LETTER_CHARS));
    }
    return lines.join('\n');
};

/**
 * Formats the recommendations the table shows as LLM context, grouped by the investigation
 * that issued them. At most {@link DETAILED_CONTEXT_LIMIT} recommendations also carry the NTSB's
 * assessment and the latest response; beyond {@link CONTEXT_LIMIT} the rest are left out and
 * the context says so.
 */
export const formatRegisterForPrompt = (recs: RegisterRecommendationDTO[], today: Date): string => {
    const detailed = recs.length <= DETAILED_CONTEXT_LIMIT;
    const included = recs.slice(0, CONTEXT_LIMIT);
    const byCase = new Map<string, RegisterRecommendationDTO[]>();
    included.forEach((rec) => {
        const key = rec.issuingCaseNumber || 'Unknown investigation';
        byCase.set(key, [...(byCase.get(key) ?? []), rec]);
    });
    const blocks = Array.from(byCase.entries()).map(([caseNumber, caseRecs]) => {
        const first = caseRecs[0];
        const where = [first.eventDate, first.eventLocation, first.caseReportNumber ? `report ${first.caseReportNumber}` : null]
            .filter((part): part is string => Boolean(part)).join('; ');
        const lines = [`Investigation ${caseNumber}${where ? ` (${where})` : ''}`];
        if (detailed && first.caseSummary) lines.push(`Synopsis: ${shorten(oneLine(first.caseSummary), CASE_TEXT_CHARS)}`);
        if (detailed && first.caseProbableCause) lines.push(`Probable cause: ${shorten(oneLine(first.caseProbableCause), CASE_TEXT_CHARS)}`);
        caseRecs.forEach((rec) => lines.push(recommendationLine(rec, today, detailed)));
        return lines.join('\n');
    });
    const notes: string[] = [];
    if (!detailed) {
        notes.push(`NOTE: ${recs.length} recommendations are shown, so NTSB letters are left out and long texts are shortened. `
            + `Filtering the table to ${DETAILED_CONTEXT_LIMIT} or fewer adds the NTSB's assessment and the latest response to each.`);
    }
    if (recs.length > CONTEXT_LIMIT) {
        notes.push(`NOTE: only the first ${CONTEXT_LIMIT} of the ${recs.length} recommendations shown are included. Narrow the filters to cover the rest.`);
    }
    return [...notes, ...blocks].join('\n\n');
};
